import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Briefcase } from 'lucide-react';

interface HoursBarChartData {
  label: string;
  hours: number;
  color?: string;
}

interface HoursBarChartProps {
  data: HoursBarChartData[];
  title?: string;
  maxBars?: number;
  className?: string;
}

// Fallback colors if CSS vars not set
const FALLBACK_COLORS = [
  '#f97316', // orange
  '#3b82f6', // blue
  '#22c55e', // green
  '#8b5cf6', // purple
  '#ec4899', // pink
];

export const HoursBarChart: React.FC<HoursBarChartProps> = ({
  data,
  title,
  maxBars = 10,
  className,
}) => {
  const { t } = useTranslation();

  const displayData = data.slice(0, maxBars);
  const remainingCount = data.length - maxBars;

  if (data.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Briefcase className="h-4 w-4 text-orange-500" />
            {title || t('dashboard.hoursByProject', 'Hours by Project')}
          </CardTitle>
        </CardHeader>
        <CardContent className="h-[200px] flex items-center justify-center text-muted-foreground">
          {t('dashboard.noProjectData', 'No project data available')}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <Briefcase className="h-4 w-4 text-orange-500" />
          {title || t('dashboard.hoursByProject', 'Hours by Project')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={Math.max(200, displayData.length * 40)}>
          <BarChart
            data={displayData}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <XAxis type="number" tickFormatter={(v) => `${v}h`} />
            <YAxis
              type="category"
              dataKey="label"
              width={100}
              tick={{ fontSize: 12 }}
              tickFormatter={(v) => v.length > 12 ? `${v.substring(0, 12)}...` : v}
            />
            <Tooltip
              formatter={(value) => [`${Number(value).toFixed(1)}h`, t('dashboard.hours', 'Hours')]}
              contentStyle={{
                backgroundColor: 'hsl(var(--background))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '6px',
              }}
            />
            <Bar dataKey="hours" radius={[0, 4, 4, 0]}>
              {displayData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={entry.color || FALLBACK_COLORS[index % FALLBACK_COLORS.length]}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
        {remainingCount > 0 && (
          <p className="text-xs text-muted-foreground text-center mt-2">
            {t('dashboard.moreProjects', '+ {{count}} more projects', { count: remainingCount })}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default HoursBarChart;
